import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { LogOut, ArrowLeft, Inbox } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Admin = () => {
  const location = useLocation();
  const [status, setStatus] = useState(location.state?.user ? "authed" : "checking");
  const [user, setUser] = useState(location.state?.user || null);
  const [enquiries, setEnquiries] = useState([]);

  useEffect(() => {
    if (location.state?.user) return;
    (async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (!res.ok) throw new Error("not authenticated");
        setUser(await res.json());
        setStatus("authed");
      } catch {
        setStatus("anon");
      }
    })();
  }, [location.state]);

  useEffect(() => {
    if (status !== "authed") return;
    fetch(`${API}/enquiries`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setEnquiries)
      .catch(() => toast.error("Could not load enquiries"));
  }, [status]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setStatus("anon");
    setUser(null);
    setEnquiries([]);
    toast.success("Signed out");
  };

  if (status === "checking") {
    return (
      <div data-testid="admin-loading" className="min-h-screen flex items-center justify-center bg-[#070709]">
        <p className="font-mono2 text-xs tracking-[0.3em] text-zinc-500 animate-pulse">VERIFYING SESSION…</p>
      </div>
    );
  }

  if (status === "anon") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#070709] px-4 relative overflow-hidden">
        <div className="absolute inset-0 lens-flare pointer-events-none" />
        <div data-testid="admin-login-card" className="relative border border-white/10 bg-[#0E0E12] p-10 sm:p-14 text-center max-w-md w-full">
          <img src="/kth-logo.png" alt="KTH Studios" className="h-16 w-auto mx-auto mb-8" />
          <h1 className="font-display text-4xl tracking-wide text-zinc-50">STUDIO ACCESS</h1>
          <p className="mt-3 text-sm text-zinc-400">Restricted area — production enquiries and pipeline data.</p>
          <button data-testid="admin-google-login-btn" onClick={login}
            className="mt-8 w-full flex items-center justify-center gap-3 bg-amber-500 text-[#070709] px-6 py-4 font-mono2 text-sm tracking-[0.2em] font-semibold hover:bg-amber-400 transition-colors">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M21.35 11.1H12v2.9h5.35c-.5 2.4-2.55 3.9-5.35 3.9a5.9 5.9 0 1 1 0-11.8c1.5 0 2.85.55 3.9 1.45l2.2-2.2A8 8 0 1 0 12 20c4.6 0 7.65-3.2 7.65-7.7 0-.4-.05-.8-.15-1.2z"/></svg>
            SIGN IN WITH GOOGLE
          </button>
          <a href="/" data-testid="admin-back-home-link" className="mt-6 inline-flex items-center gap-2 font-mono2 text-[11px] tracking-[0.25em] text-zinc-500 hover:text-amber-500 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> BACK TO SITE
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#070709] px-4 sm:px-8 lg:px-16 py-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-6">
          <div className="flex items-center gap-4">
            <img src="/kth-logo.png" alt="KTH Studios" className="h-12 w-auto" />
            <div>
              <h1 className="font-display text-3xl tracking-wide text-zinc-50">PRODUCTION ENQUIRIES</h1>
              <p className="font-mono2 text-[10px] tracking-[0.25em] text-zinc-500 mt-1">SIGNED IN AS <span className="text-amber-500">{user?.email}</span></p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-view-site-link" className="font-mono2 text-[11px] tracking-[0.2em] border border-white/20 px-4 py-2.5 text-zinc-300 hover:border-amber-500 hover:text-amber-500 transition-colors">VIEW SITE</a>
            <button data-testid="admin-logout-btn" onClick={logout}
              className="flex items-center gap-2 font-mono2 text-[11px] tracking-[0.2em] border border-red-500/40 px-4 py-2.5 text-red-400 hover:bg-red-500/10 transition-colors">
              <LogOut className="w-3.5 h-3.5" /> SIGN OUT
            </button>
          </div>
        </div>

        <div data-testid="admin-enquiries-list" className="mt-8 space-y-4">
          {enquiries.length === 0 && (
            <div className="border border-white/10 bg-[#0E0E12] p-12 text-center">
              <Inbox className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
              <p className="font-mono2 text-xs tracking-[0.25em] text-zinc-500">NO ENQUIRIES YET</p>
            </div>
          )}
          {enquiries.map((e) => (
            <div key={e.id} data-testid={`admin-enquiry-${e.id}`} className="border border-white/10 bg-[#0E0E12] p-6 hover:border-amber-500/40 transition-colors">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-heading text-lg font-bold text-zinc-100">{e.name}</h3>
                  <p className="font-mono2 text-[11px] tracking-[0.15em] text-zinc-400 mt-1">
                    {e.email}{e.company ? ` — ${e.company}` : ""}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="font-mono2 text-[10px] tracking-[0.2em] bg-amber-500/10 border border-amber-500/40 text-amber-400 px-3 py-1 uppercase">{e.project_type}</span>
                  {e.budget && <span className="font-mono2 text-[10px] tracking-[0.2em] border border-white/15 text-zinc-300 px-3 py-1 uppercase">{e.budget}</span>}
                </div>
              </div>
              <p className="mt-4 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{e.message}</p>
              <p className="mt-4 font-mono2 text-[10px] tracking-[0.2em] text-zinc-600">
                RECEIVED {new Date(e.created_at).toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
